/**
 * QualificationsPendingWidget — Card compacte pour la Vue d'ensemble Super Admin.
 *
 * Affiche le compteur de justificatifs de qualifications en attente de validation
 * et propose un raccourci vers l'onglet "Validation manuelle" plein écran.
 *
 * Endpoint : GET /api/admin/qualifications/pending?limit=1&offset=0 → renvoie `{ total, items }`.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Award, ArrowRight, Clock, Loader2 } from 'lucide-react';
import axios from 'axios';
import { getAuthHeader } from '../services/tokenService';

const API = process.env.REACT_APP_BACKEND_URL;

export const QualificationsPendingWidget = ({ onOpen }) => {
  const [total, setTotal] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // On charge en parallèle qualifications + dossiers d'arbitrage
        const [qRes, dRes] = await Promise.all([
          axios.get(`${API}/api/admin/qualifications/pending?limit=5&offset=0`,
            { headers: getAuthHeader() }).catch(() => ({ data: { total: 0, items: [] } })),
          axios.get(`${API}/api/admin/dossiers-arbitrage/pending?limit=5&offset=0`,
            { headers: getAuthHeader() }).catch(() => ({ data: { total: 0, items: [] } })),
        ]);
        if (cancelled) return;
        const qTotal = qRes.data?.total || 0;
        const dTotal = dRes.data?.total || 0;
        setTotal(qTotal + dTotal);
        // On combine les items pour l'aperçu (max 5)
        const combined = [
          ...(qRes.data?.items || []).map((i) => ({ ...i, _source: 'qualification' })),
          ...(dRes.data?.items || []).map((i) => ({ ...i, _source: 'arbitrage' })),
        ].slice(0, 5);
        setRecent(combined);
      } catch {
        if (!cancelled) setTotal(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const hasPending = total > 0;
  const colorClasses = hasPending
    ? 'from-amber-50 to-white border-amber-200'
    : 'from-emerald-50 to-white border-emerald-200';
  const iconBgClasses = hasPending ? 'bg-amber-100' : 'bg-emerald-100';
  const iconColorClass = hasPending ? 'text-amber-600' : 'text-emerald-600';
  const numColorClass = hasPending ? 'text-amber-700' : 'text-emerald-700';

  return (
    <Card
      className={`bg-gradient-to-br ${colorClasses} hover:shadow-lg transition-all`}
      data-testid="qualifications-pending-widget"
    >
      <CardContent className="pt-5 pb-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4 min-w-0 flex-1">
            <div className={`w-14 h-14 rounded-2xl ${iconBgClasses} flex items-center justify-center shrink-0`}>
              <Award className={`h-7 w-7 ${iconColorClass}`} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-baseline gap-2">
                {loading ? (
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                ) : (
                  <span className={`text-3xl font-bold ${numColorClass}`} data-testid="qualifs-pending-count">
                    {total}
                  </span>
                )}
                <span className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
                  Validation manuelle
                </span>
              </div>
              <div className="text-sm font-medium text-slate-700 mt-0.5">
                Qualifications en attente de validation
              </div>
              {!loading && hasPending && recent.length > 0 && (
                <div className="mt-2 space-y-0.5">
                  {recent.slice(0, 3).map((d) => (
                    <div key={d.id} className="flex items-center gap-1.5 text-[11px] text-slate-600 truncate">
                      <Clock className="h-3 w-3 text-amber-500 shrink-0" />
                      <span className="font-medium truncate">
                        {d.membre_entreprise || `${d.membre_prenom || ''} ${d.membre_nom || ''}`.trim() || 'Membre'}
                      </span>
                      <span className="text-slate-400">·</span>
                      <span className="truncate">{d.qualification_label || d.qualification_key}</span>
                    </div>
                  ))}
                  {total > 3 && (
                    <div className="text-[11px] text-slate-500 italic">
                      + {total - 3} autres dossier{total - 3 > 1 ? 's' : ''} à traiter
                    </div>
                  )}
                </div>
              )}
              {!loading && !hasPending && (
                <div className="text-xs text-emerald-700 mt-0.5">
                  Aucun dossier en attente ✓
                </div>
              )}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onOpen}
            className="shrink-0 text-slate-700 hover:bg-white/60"
            data-testid="open-validation-manuelle-tab"
          >
            Voir tous
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default QualificationsPendingWidget;
